import React, { useState, useRef, useEffect } from 'react';
import { Combobox } from '@headlessui/react';
import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { twMerge } from 'tailwind-merge';
import ParticipantItem from '../../search/ParticipantItem';
import { PlusIcon } from 'lucide-react';

export interface Participant {
  id: number;
  name?: string;
  email: string;
  role?: string;
  imageUrl?: string;
  sb_recipient_id?: number;
  action?: number;
}

interface MultiSelectInputProps {
  participants: Participant[];
  selectedParticipants: Participant[];
  onSearch: (query: string) => void;
  onAddParticipant: (participant: Participant) => void;
  onRemoveParticipant: (participant: Participant) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const MultiSelectInput: React.FC<MultiSelectInputProps> = ({
  participants,
  selectedParticipants,
  onSearch,
  onAddParticipant,
  onRemoveParticipant,
  placeholder = "Search and add recipients...",
  disabled = false,
  className = "",
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuery(value);
    onSearch(value);
    setIsOpen(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Backspace' && query === '' && selectedParticipants.length > 0) {
      // Remove last participant when backspace is pressed on empty input
      const lastParticipant = selectedParticipants[selectedParticipants.length - 1];
      onRemoveParticipant(lastParticipant);
    }
  };

  const handleParticipantSelect = (participant: Participant|string) => {
    if (typeof participant === 'string') {
      return;
    }
    onAddParticipant(participant);
    setQuery('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleInputBlur = () => {
    // Delay closing to allow for option selection
    setTimeout(() => setIsOpen(false), 200);
  };

  const filteredParticipants = participants.filter(participant => 
    !selectedParticipants.some(selected => selected.email === participant.email)
  );
  function isValidEmail(email) {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    return emailRegex.test(email);
  }
  return (
    <div className={twMerge('relative', className)}>
      <Combobox value={null} onChange={handleParticipantSelect}>
        <div className="relative">
          <div className={twMerge(
            "min-h-10 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus-within:ring-1",
            disabled ? "bg-gray-50 cursor-not-allowed" : "bg-white"
          )}>
            <div className="flex flex-wrap gap-1.5 items-center pr-7">
              {/* Selected participants as chips */}
              {selectedParticipants.map((participant) => (
                <div
                  key={participant.id || participant.email}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 border border-blue-700 rounded-full text-sm flex-shrink-0"
                >
                  <span className="text-blue-800 font-medium">
                    {participant.name || participant.email}
                  </span>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => onRemoveParticipant(participant)}
                      className="ml-1 text-blue-600 hover:text-blue-800 focus:outline-none"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}

              {/* Search input */}
              <div className={twMerge(
                "flex-grow max-w-full",
                selectedParticipants.length === 0 && "w-full"
              )}>
                {disabled && selectedParticipants.length === 0 ? (
                  <div className="text-sm text-gray-500 py-1 leading-6 break-words">
                    {placeholder}
                  </div>
                ) : (
                  <Combobox.Input
                    ref={inputRef}
                    className={twMerge(
                      "w-full border-0 outline-none bg-transparent text-sm placeholder-gray-400 break-all",
                      disabled && "cursor-not-allowed"
                    )}
                    placeholder={selectedParticipants.length === 0 ? placeholder : ''}
                    value={query}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                  />
                )}
              </div>
            </div>

            {/* Dropdown button */}
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </Combobox.Button>
          </div>

          {/* Dropdown options */}
          {isOpen && (
            <Combobox.Options static className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filteredParticipants.length === 0 && query !== '' ? (
                <Combobox.Option
                  value="Add External Recipient"
                  onClick={() => {
                    if (!isValidEmail(query)) {
                      return;
                      <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                        No participants found.
                      </div>;
                    }
                    const newParticipant = { id: Date.now(), email: query.trim(), action: 0 };
                    handleParticipantSelect(newParticipant);
                  }}
                  className={({ active }) =>
                    twMerge(
                      'relative cursor-default select-none py-2 pl-3 pr-9',
                      active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                    )
                  }
                >
                  {({ active }) => (
                    <span
                      className={twMerge(
                        'relative cursor-default select-none py-4 pl-5 pr-9 flex items-center',
                        active ? 'bg-blue-200 text-black' : 'text-gray-700'
                      )}
                    >
                      {!isValidEmail(query) && (
                        <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                          No participants found.
                        </div>
                      )}
                      {isValidEmail(query) && (
                        <>
                          <PlusIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                          Add External Recipient {query.trim()}
                        </>
                      )}
                    </span>
                  )}
                </Combobox.Option>
              ) : (
                // <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                //   No participants found.
                // </div>
                filteredParticipants.map((participant) => (
                  <Combobox.Option
                    key={participant.id || participant.email}
                    value={participant}
                    className={({ active }) =>
                      twMerge(
                        'relative cursor-default select-none py-2 pl-3 pr-9',
                        active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                      )
                    }
                  >
                    {({ selected }) => (
                      <div className="flex items-center">
                        <ParticipantItem
                          imageUrl={participant.imageUrl}
                          name={participant.name}
                          email={participant.email}
                          role={participant.role}
                        />
                        {selected && (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                        )}
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          )}
        </div>
      </Combobox>
    </div>
  );
};

export default MultiSelectInput;
